(() => {
  const FORM_SELECTOR = 'form[data-spot-quickadd-form]';
  const HOME_GRID_SELECTOR = '.home-products-grid';
  const HOVER_CARD_SELECTOR = '.spot-card[data-spot-hover-images]';
  const HOVER_CYCLE_MS = 3000;
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const activeHoverTimers = new WeakMap();

  const findCartTarget = () =>
    document.querySelector('.spot-site-header .header-actions__cart-icon') ||
    document.querySelector('.spot-site-header .action__cart') ||
    document.querySelector('.spot-site-header [href*="/cart"]');

  const pulseCartTarget = (target) => {
    const pulseTarget = target?.closest('.header-actions__action') || target;
    if (!(pulseTarget instanceof HTMLElement)) return;

    pulseTarget.classList.remove('spot-cart-pulse');
    void pulseTarget.offsetWidth;
    pulseTarget.classList.add('spot-cart-pulse');
    window.setTimeout(() => pulseTarget.classList.remove('spot-cart-pulse'), 260);
  };

  const renderCartCount = (itemCount) => {
    const nextCount = Number(itemCount) || 0;
    const countText = nextCount > 99 ? '99+' : `${nextCount}`;

    document.querySelectorAll('.header-actions__cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);
    });

    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', nextCount <= 0);
      const countNode = bubble.querySelector('.cart-bubble__text-count');

      bubble.classList.add('cart-bubble--animating');
      window.setTimeout(() => bubble.classList.remove('cart-bubble--animating'), 240);

      if (!(countNode instanceof HTMLElement)) return;
      if (nextCount > 0) {
        countNode.textContent = countText;
        countNode.classList.remove('hidden');
      } else {
        countNode.textContent = '';
        countNode.classList.add('hidden');
      }
    });
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });
    if (!response.ok) return null;
    return response.json();
  };

  const emitCartUpdate = (cart) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          data: {
            itemCount: Number(cart?.item_count) || 0,
            source: 'spot-card-quickadd',
          },
        },
      })
    );
  };

  const animateFlyToCart = (sourceImage, destination) => {
    if (reduceMotionQuery.matches) return;
    if (!(sourceImage instanceof HTMLImageElement) || !(destination instanceof Element)) return;

    const sourceRect = sourceImage.getBoundingClientRect();
    const destinationRect = destination.getBoundingClientRect();
    if (!sourceRect.width || !sourceRect.height || !destinationRect.width || !destinationRect.height) return;

    const flyer = sourceImage.cloneNode(true);
    if (!(flyer instanceof HTMLImageElement)) return;

    flyer.classList.add('spot-flyer-image');
    flyer.style.position = 'fixed';
    flyer.style.left = `${sourceRect.left}px`;
    flyer.style.top = `${sourceRect.top}px`;
    flyer.style.width = `${sourceRect.width}px`;
    flyer.style.height = `${sourceRect.height}px`;
    flyer.style.borderRadius = '18px';
    flyer.style.pointerEvents = 'none';
    flyer.style.objectFit = 'cover';
    flyer.style.zIndex = '9999';
    flyer.style.transformOrigin = 'center';

    document.body.appendChild(flyer);

    const deltaX = (destinationRect.left + destinationRect.width / 2) - (sourceRect.left + sourceRect.width / 2);
    const deltaY = (destinationRect.top + destinationRect.height / 2) - (sourceRect.top + sourceRect.height / 2);

    const animation = flyer.animate(
      [
        {
          transform: 'translate3d(0, 0, 0) scale(1)',
          opacity: 0.95,
        },
        {
          transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.18)`,
          opacity: 0.2,
        },
      ],
      {
        duration: 620,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      }
    );

    animation.finished
      .catch(() => null)
      .finally(() => {
        flyer.remove();
      });
  };

  const onQuickAddSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
    if (form.closest(HOME_GRID_SELECTOR)) return;

    const submitButton = form.querySelector('[data-spot-quickadd-button]');
    if (!(submitButton instanceof HTMLButtonElement)) return;
    if (submitButton.disabled) return;

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const variantId = Number(variantInput?.value);
    const quantity = Number(quantityInput?.value || 1);

    if (!variantId || Number.isNaN(variantId)) return;

    event.preventDefault();
    event.stopPropagation();

    submitButton.disabled = true;
    form.setAttribute('data-loading', 'true');

    try {
      const cartSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.getAttribute('data-section-id'))
        .filter(Boolean);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity > 0 ? quantity : 1,
          sections: cartSections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Quick add failed');

      const card = form.closest('.spot-card');
      const sourceImage = card?.querySelector('.spot-card__image');
      const cartTarget = findCartTarget();
      animateFlyToCart(sourceImage, cartTarget);

      if (window.SpotCartSync?.syncCartUI) {
        await window.SpotCartSync.syncCartUI({
          source: 'spot-card-quickadd',
          openDrawer: true,
          openDelay: 220,
        });
      } else {
        const cart = await fetchCart();
        if (cart) {
          renderCartCount(cart.item_count);
          emitCartUpdate(cart);
        }
        pulseCartTarget(findCartTarget());
      }
    } catch (error) {
      console.error(error);
    } finally {
      form.removeAttribute('data-loading');
      submitButton.disabled = false;
    }
  };

  const normalizeImageUrl = (url) => {
    if (typeof url !== 'string' || url.trim() === '') return '';
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    return url;
  };

  const resetCardImage = (card) => {
    const state = activeHoverTimers.get(card);
    if (!state || !(state.image instanceof HTMLImageElement)) return;

    state.image.src = state.baseSrc;
    state.image.srcset = state.baseSrcset;
    state.image.sizes = state.baseSizes;
    state.index = 0;
  };

  const stopCardCycle = (card) => {
    const state = activeHoverTimers.get(card);
    if (!state) return;
    if (state.timer) {
      window.clearInterval(state.timer);
      state.timer = null;
    }
    resetCardImage(card);
  };

  const startCardCycle = (card) => {
    if (reduceMotionQuery.matches) return;
    const state = activeHoverTimers.get(card);
    if (!state || state.timer || state.urls.length <= 1) return;

    state.timer = window.setInterval(() => {
      state.index = (state.index + 1) % state.urls.length;
      const nextUrl = state.urls[state.index];
      state.image.srcset = '';
      state.image.sizes = '';
      state.image.src = nextUrl;
    }, HOVER_CYCLE_MS);
  };

  const initHoverCycles = (scope = document) => {
    scope.querySelectorAll(HOVER_CARD_SELECTOR).forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      if (card.dataset.spotHoverCycleReady === 'true') return;

      const image = card.querySelector('.spot-card__image');
      const rawUrls = (card.getAttribute('data-spot-hover-images') || '')
        .split('||')
        .map((url) => normalizeImageUrl(url))
        .filter(Boolean);

      if (!(image instanceof HTMLImageElement) || rawUrls.length <= 1) {
        card.dataset.spotHoverCycleReady = 'true';
        return;
      }

      activeHoverTimers.set(card, {
        image,
        urls: rawUrls,
        timer: null,
        index: 0,
        baseSrc: image.currentSrc || image.src,
        baseSrcset: image.getAttribute('srcset') || '',
        baseSizes: image.getAttribute('sizes') || '',
      });

      card.addEventListener('mouseenter', () => startCardCycle(card));
      card.addEventListener('mouseleave', () => stopCardCycle(card));
      card.addEventListener('focusout', () => {
        if (!card.matches(':hover')) {
          stopCardCycle(card);
        }
      });

      card.dataset.spotHoverCycleReady = 'true';
    });
  };

  document.addEventListener('submit', onQuickAddSubmit);
  initHoverCycles();
  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    initHoverCycles(event.target);
  });
})();
